import { Clock, MessageSquare, User } from 'lucide-react';
import QualityBadge from '@/components/ui/QualityBadge';
import calculateConversationDuration from '@/utils/calculateConversationDuration';
import useTranslation from '@/hooks/useTranslation';
import { AllTranscriptsProps } from '@/types';
import {
  HIDDEN_METADATA_KEYS,
  formatMetadataCellValue,
  isRenderableMetadataValue,
} from '@/utils/metadataDisplay';

interface TranscriptInfoStripProps {
  conversation?: AllTranscriptsProps | null;
  totalMessages?: number;
  className?: string;
}

const statusColors: Record<string, string> = {
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  abandoned: 'bg-amber-50 text-amber-700 border-amber-200',
  in_progress: 'bg-blue-50 text-blue-700 border-blue-200',
  screening_failed: 'bg-red-50 text-red-700 border-red-200',
  digested: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  done: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  analyzing: 'bg-blue-50 text-blue-700 border-blue-200',
};

// Map raw DB status strings to translation keys.
const statusTranslationKeys: Record<string, string> = {
  completed: 'msg.conversationStatusCompleted',
  abandoned: 'msg.conversationStatusAbandoned',
  in_progress: 'msg.conversationStatusInProgress',
  screening_failed: 'msg.conversationStatusScreeningFailed',
  digested: 'msg.conversationStatusDigested',
  done: 'msg.conversationStatusDone',
  analyzing: 'msg.conversationStatusAnalyzing',
};

/**
 * Compact info strip rendered ABOVE the chat on narrow transcript
 * detail views (≤800px). Surfaces the essential metadata a researcher
 * needs upfront — participant number, status, duration, total messages,
 * and the first couple of screening metadata tags — without forcing
 * them to scroll past the chat to find out how long a conversation
 * lasted or who the respondent is. The full ConversationMetadata card
 * (including the long AI summary) still renders below the chat.
 *
 * Hidden by default; the caller passes `className="hidden md800:flex"`
 * to make it visible only at narrow widths.
 */
function TranscriptInfoStrip({
  conversation,
  totalMessages,
  className = '',
}: TranscriptInfoStripProps) {
  const { lang } = useTranslation();

  const status = conversation?.status ?? '';
  const statusStyle = statusColors[status] || 'bg-neutral-50 text-neutral-700 border-neutral-200';

  const hasDuration = !!(conversation?.startedAt && conversation?.finishedAt);
  const duration = hasDuration
    ? calculateConversationDuration({
        startedAt: conversation?.startedAt as string,
        finishedAt: conversation?.finishedAt as string,
      })
    : null;

  // Surface up to 2 screening metadata tags so the researcher can spot
  // a respondent's key attributes (age, gender, country, etc.) without
  // scrolling. Skip internal keys like computed_quality.
  const metadataTags = conversation?.metadata
    ? Object.entries(conversation.metadata)
        .filter(
          ([key, value]) => !HIDDEN_METADATA_KEYS.has(key) && isRenderableMetadataValue(value),
        )
        .slice(0, 2)
    : [];

  return (
    <div
      className={`flex flex-wrap items-center gap-2 px-4 py-3 rounded-xl border border-neutral-200/80 bg-white shadow-sm ${className}`}
    >
      {/* Participant */}
      {conversation?.respondent_index !== undefined && (
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-neutral-800">
          <User className="h-3.5 w-3.5 text-neutral-400" />
          {lang.get('msg.participant')} #{conversation?.respondent_index}
        </span>
      )}

      {/* Status badge */}
      {status && (
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium capitalize border ${statusStyle}`}
        >
          {statusTranslationKeys[status]
            ? lang.get(statusTranslationKeys[status])
            : status.replace(/_/g, ' ')}
        </span>
      )}

      {/* TELV1-433 — judged quality (reason on hover). */}
      {conversation?.quality?.status === 'complete' && (
        <QualityBadge quality={conversation.quality} />
      )}

      {/* Duration */}
      {duration && (
        <span className="inline-flex items-center gap-1.5 text-xs text-neutral-600">
          <Clock className="h-3.5 w-3.5 text-neutral-400" />
          {duration}
        </span>
      )}

      {/* Total messages */}
      {typeof totalMessages === 'number' && totalMessages > 0 && (
        <span className="inline-flex items-center gap-1.5 text-xs text-neutral-600">
          <MessageSquare className="h-3.5 w-3.5 text-neutral-400" />
          {totalMessages}
        </span>
      )}

      {/* Screening metadata tags — key: value chips */}
      {metadataTags.map(([key, value]) => (
        <span
          key={key}
          className="inline-flex items-center gap-1 text-[11px] text-neutral-600 bg-neutral-50 border border-neutral-200 px-2 py-0.5 rounded-full"
        >
          <span className="text-neutral-400">{key}:</span>
          <span className="font-medium">{formatMetadataCellValue(value)}</span>
        </span>
      ))}
    </div>
  );
}

export default TranscriptInfoStrip;
